// mui.config.tsx
'use client';

import { neue } from '@helpers/fonts/fonts'

import { blue, common } from '@mui/material/colors'
import { createTheme, responsiveFontSizes } from '@mui/material/styles'
import { PaletteColorOptions } from '@mui/material/styles/createPalette'

declare module '@mui/material/styles' {
  interface Palette {
    white?: PaletteColorOptions
    blue?: PaletteColorOptions
    gray?: PaletteColorOptions
  }
  interface PaletteOptions {
    white?: PaletteColorOptions
    blue?: PaletteColorOptions
    gray?: PaletteColorOptions
  }
}

declare module "@mui/material" {
  interface ButtonPropsColorOverrides {
    white: true,
    blue: true,
    gray: true,
  }
}
declare module '@mui/material/styles/createPalette' {
    interface PaletteOptions {
        white?: PaletteColorOptions,
        blue?: PaletteColorOptions,
        gray?: PaletteColorOptions,
    }

    interface Palette {
        white: PaletteColor,
        blue: PaletteColor,
        gray: PaletteColor,
    }
}

declare module '@mui/material/styles' {
  interface TypographyVariants {
    size34: React.CSSProperties
    size24: React.CSSProperties
    size20: React.CSSProperties
    size18: React.CSSProperties
    size16: React.CSSProperties 
    size15: React.CSSProperties
    size14: React.CSSProperties
    size12: React.CSSProperties
    size10: React.CSSProperties
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    size34?: React.CSSProperties
    size24?: React.CSSProperties
    size20?: React.CSSProperties
    size18?: React.CSSProperties
    size16?: React.CSSProperties
    size15?: React.CSSProperties
    size14?: React.CSSProperties
    size12?: React.CSSProperties
    size10?: React.CSSProperties
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    size34: true
    size24: true
    size20: true
    size18: true
    size16: true
    size15: true
    size14: true
    size12: true
    size10: true
  }
}

const palette = {
  primary: {
    light: '#6B7680',
    main: '#212121',
  },
  secondary: {
    main: "#0C43A5",
  },
  warning: {
    main: '#d7de00',
  },
  error: {
    main: "#f30909",
  },
  orange: {
    main: '#ff8000'
  },
  white: {
    main: common.white,
  },
  gray: {
    main: '#d1d1d1',
    light: '#f5f5f5',
  },
}

// Define breakpoints una sola vez para usar en ambos temas
const customBreakpoints = {
  values: {
    xs: 0,
    sm: 550,
    md: 890,
    lg: 1200,
    xl: 1536,
  },
};

const globalTheme: any = createTheme({
  breakpoints: customBreakpoints, // <-- Añadido aquí
  palette,
  spacing: 4,
} as any)

let customTheme = createTheme(
  {
    breakpoints: customBreakpoints, // <-- Y aquí también
    typography: {
      fontFamily: neue.style.fontFamily,
      // Para añadir nuevos tamaños en rem: Nº_px/16
      size34: {
        fontSize: '1.75rem', // 28px
        lineHeight: '1.25',
        [globalTheme.breakpoints.up('md')]: {
          fontSize: '2.125rem', // 34px
        },
      },
      size24: {
        fontSize: '1.375rem', // 22px
        lineHeight: '1.25',
        [globalTheme.breakpoints.up('md')]: {
          fontSize: '1.5rem', // 24px
        },
      },
      size20: {
        fontSize: '1.125rem', // 18px
        lineHeight: '1.3',
        [globalTheme.breakpoints.up('md')]: {
          fontSize: '1.25rem', // 20px
        },
      },
      size18: {
        fontSize: '1.125rem',
      },
      size16: {
        fontSize: '1rem',
      },
      size15: {
        fontSize: '0.9375rem',
      },
      size14: {
        fontSize: '0.875rem',
      },
      size12: {
        fontSize: '0.75rem',
      },
      size10: {
        fontSize: '0.625rem',
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: `
          h1, h2, h3, h4, h5, h6, p, span, input, textarea {
            font-family: ${neue.style.fontFamily};
          }
        `,
      }, 
      MuiContainer: {
        styleOverrides: {
          root: {
            paddingLeft: globalTheme.spacing(4) + ' !important',
            paddingRight: globalTheme.spacing(4) + ' !important',
            [globalTheme.breakpoints.up('md')]: {
              paddingLeft: globalTheme.spacing(6) + ' !important',
              paddingRight: globalTheme.spacing(6) + ' !important',
            },
          },
          maxWidthXl: {
            [globalTheme.breakpoints.up('lg')]: {
              maxWidth: `1500px`,
            },
            [globalTheme.breakpoints.up('xl')]: {
              maxWidth: `1920px`,
            },
          },
        },
      },
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            borderRadius: 0,
          }
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 0,
          }
        }
      },
      MuiCheckbox: {
        defaultProps: {
          disableRipple: true,
        },
        styleOverrides: {
          root: {
            padding: '4px 9px',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            lineHeight: 1.25,
            boxShadow: 'none',
            ':hover': {
              boxShadow: 'none',
            },
            '&.MuiButton-containedSecondary': {
              '&:hover': {
                backgroundColor: globalTheme.palette.text.primary,
                color: '#fff'
              },
            },
          },
          sizeLarge: {
            height: '40px',
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: ({ theme, ownerState }) => ({
            ...(ownerState.color === 'primary' && {
              color: blue['500'],
              textDecorationColor: blue['500'],
            }),
          }),
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            fontFamily: neue.style.fontFamily,
            borderRadius: 0,
          },
        },
      },
      MuiBadge: {
        styleOverrides: {
          badge: {
            fontFamily: neue.style.fontFamily,
            height: "17px",
            minWidth: "17px",
            padding: "0",
          }
        }
      },
      MuiAlertTitle: {
        styleOverrides: {
          root: {
            fontFamily: neue.style.fontFamily,
          },
        },
      },
      MuiList: {
        styleOverrides: {
          root: {
            padding: 0,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontFamily: neue.style.fontFamily,
          }
        },
      },
      MuiPaginationItem: {
        styleOverrides: {
          root: {
            fontFamily: neue.style.fontFamily,
          }
        }
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            fontFamily: neue.style.fontFamily,
          }
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontFamily: neue.style.fontFamily,
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: globalTheme.palette.secondary.main,
            fontFamily: neue.style.fontFamily,
            fontWeight: 500,
            borderRadius: 0,
          },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: {
            [globalTheme.breakpoints.down('md')]: {
              minWidth: '1000px',
            }
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            boxShadow: 'none',
            overflowX: 'auto',
            [globalTheme.breakpoints.down('md')]: {
              width: '100vw',
              maxWidth: '100%',
              paddingBottom: '12px',
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            backgroundColor: "#000",
            fontFamily: neue.style.fontFamily,
            padding: '8px 10px',
            textAlign: "center",
            lineHeight: 1.15,
            fontWeight: '500',
            fontSize: "14px",
            color: "#fff",
            textTransform: 'uppercase',
          },
          body: {
            fontFamily: neue.style.fontFamily,
            padding: '4px 8px',
          },
        },
      },
      MuiBreadcrumbs: {
        styleOverrides: {
          separator: {
            marginRight: '6px',
            marginLeft: '6px',
          },
          li: {
            ':last-child': {
              color: globalTheme.palette.text.primary,
            },
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            paddingTop: `28px`,
            paddingRight: `60px`,
            lineHeight: 1.15,
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            paddingTop: `64px`,
            paddingLeft: "16px",
            paddingRight: "16px",
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: `24px 20px`,
          },
        },
      },
    },
  },
  globalTheme
)

customTheme = responsiveFontSizes(customTheme)

export default customTheme

